import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { X } from 'lucide-react'
import useBodyScrollLock from '../../hooks/useBodyScrollLock'

// App-style bottom sheet: slides up from the bottom, drag-to-dismiss, backdrop,
// focus trap, Esc to close, body scroll lock, safe-area aware.
const BottomSheet = ({ open, onClose, title, children, dismissible = true }) => {
  const panelRef = useRef(null)
  const lastFocused = useRef(null)
  const reduce = useReducedMotion()

  useBodyScrollLock(open)

  // Focus management: trap focus, Esc to close, restore focus on close.
  useEffect(() => {
    if (!open) return
    lastFocused.current = document.activeElement
    const panel = panelRef.current
    panel?.focus()

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && dismissible) {
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panel) return
      const focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusables.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      lastFocused.current?.focus?.()
    }
  }, [open, dismissible, onClose])

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center md:items-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={dismissible ? onClose : undefined}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
          />

          {/* Panel */}
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={title || 'Dialog'}
            tabIndex={-1}
            initial={reduce ? { opacity: 0 } : { y: '100%' }}
            animate={reduce ? { opacity: 1 } : { y: 0 }}
            exit={reduce ? { opacity: 0 } : { y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            drag={dismissible && !reduce ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 120 || info.velocity.y > 600) onClose()
            }}
            className="relative w-full bg-sc-lowest shadow-[var(--elev-4)] md:max-w-lg max-h-[88vh] overflow-y-auto rounded-t-5xl md:rounded-5xl
                       px-5 pt-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))] outline-none focus-visible:outline-none"
          >
            {/* Drag handle */}
            <div className="mx-auto mb-3 h-1.5 w-11 rounded-full bg-outline md:hidden" />

            <div className="flex items-center justify-between mb-4">
              {title ? (
                <h3 className="text-lg font-semibold text-ink">{title}</h3>
              ) : (
                <span />
              )}
              {dismissible && (
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="flex items-center justify-center w-9 h-9 -mr-1 rounded-xl text-ink-3 hover:text-ink-2 active:scale-95 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default BottomSheet
